import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { User } from '../models/User';
import { fromUnixTime, isAfter } from 'date-fns';
import { tap } from 'rxjs/operators';
import { Router } from '@angular/router';

@Injectable({
    providedIn: 'root'
})
export class UserService {
    isAuthenticatedSubject = new BehaviorSubject<boolean>(this.isJwtAuthenticated());
    currentUserSubject = new BehaviorSubject<User>(null);
    countUserSubject = new BehaviorSubject<number>(0);
    constructor(private http: HttpClient, private router: Router) {

    }

    async login(username: string, password: string): Promise<boolean> {
        const data = {
            username: username,
            password: password
        };

        try {
            const response = await this.http.post<any>('api:///command/login', data).toPromise();
            this.setAccessToken(response.accessToken);
            this.setRefreshToken(response.refreshToken);
            this.updateIsAuthenticatedSubject(true);

            return true;
        } catch (error) {
            this.updateIsAuthenticatedSubject(false);
            this.updateCurrentUser(null);
            if (error.status === 401) {
                // First login, the user has to change the password
                return false;
            }

            throw error.status === 400
                ? error.error.validationErrors[0].errorMessage
                : 'Unknown error normal login';
        }
    }

    async firstLogin(username: string, newPassword: string): Promise<boolean> {
        const data = {
            username: username,
            newPassword: newPassword
        };

        try {
            const response = await this.http.post<any>('api:///command/FirstLogin', data).toPromise();
            this.setAccessToken(response.accessToken);
            this.setRefreshToken(response.refreshToken);
            this.updateIsAuthenticatedSubject(true);

            return true;
        } catch (error) {
            this.updateIsAuthenticatedSubject(false);
            this.updateCurrentUser(null);
            throw error.status === 400
                ? error.error.validationErrors[0].errorMessage
                : 'Unknown error first login';
        }
    }

    logout() {
        if (this.hasAccessToken()) {
            localStorage.removeItem(this.getAccessTokenKey());
            localStorage.removeItem(this.getRefreshTokenKey());
        }

        this.updateIsAuthenticatedSubject(false);
        this.updateCurrentUser(null);

        return Promise.resolve(true);
    }

    async getCurrentUser(): Promise<User> {
        try {
            const data = await this.http.get('api:///query/CurrentUser').toPromise();
            let user = null;
            if (data) {
                user = new User(data);
            }

            user.hasAnyNotifications = <any>await this.http.post('api:///query/HasActiveNotifications', {}).toPromise();

            this.updateIsAuthenticatedSubject(!!data);
            this.updateCurrentUser(user);

            return user;
        } catch (error) {
            this.updateIsAuthenticatedSubject(false);
            this.updateCurrentUser(null);

            throw error;
        }
    }

    async getCountUser(): Promise<number> {
        try {
            const data = await this.http.get('api:///query/RequestCount').toPromise();
            let countUser = null;
            if (data) {
                countUser = data;
            }

            this.updateCount(countUser);

            return countUser;
        } catch (error) {
            throw error;
        }
    }

    refreshAccessToken() {
        return this.http
            .post<any>('api:///command/RefreshAccessToken', {
                refreshToken: this.getRefreshToken()
            })
            .pipe(tap(response => {
                this.setAccessToken(response.accessToken);
                this.setRefreshToken(response.refreshToken);
            }));
    }

    getAccessTokenKey() {
        return environment.accessToken;
    }

    getRefreshTokenKey() {
        return environment.refreshToken;
    }

    getAccessToken() {
        return localStorage.getItem(this.getAccessTokenKey());
    }

    getRefreshToken() {
        return localStorage.getItem(this.getRefreshTokenKey());
    }

    hasAccessToken() {
        return !!this.getAccessToken();
    }

    setAccessToken(accessToken: string) {
        return localStorage.setItem(this.getAccessTokenKey(), accessToken);
    }

    setRefreshToken(refreshToken: string) {
        return localStorage.setItem(this.getRefreshTokenKey(), refreshToken);
    }

    private updateIsAuthenticatedSubject(value) {
        if (this.isAuthenticatedSubject.getValue() !== value) {
            this.isAuthenticatedSubject.next(value);
        }
    }

    private updateCurrentUser(user) {
        if (this.currentUserSubject.getValue() !== user) {
            this.currentUserSubject.next(user);
        }
    }

    private updateCount(count) {
        if (this.countUserSubject.getValue() !== count) {
            this.countUserSubject.next(count);
        }
    }

    private parseJwt(token): any {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));

        return JSON.parse(jsonPayload);
    }

    getJwtPayload() {
        const token = localStorage.getItem(environment.accessToken);
        if (!token) {
            return null;
        }

        return this.parseJwt(token);
    }

    private isJwtAuthenticated() {
        try {
            const payload = this.getJwtPayload();

            if (!payload) {
                return false;
            }

            const expireDate = fromUnixTime(payload.exp);

            return isAfter(expireDate, new Date());
        } catch (ex) {
            console.error(ex);
            return false;
        }
    }

    createUser(userSaveData): Observable<any> {
        return this.http.post('api:///command/CreateUser', userSaveData);
    }

    createOrganization(organizationSaveData): Observable<any> {
        return this.http.post('api:///command/CreateOrganization', organizationSaveData);
    }

    getOrganizations(): Observable<any> {
        return this.http.get('api:///query/CustomDisplayModel');
    }

    async isEmailUniq(email): Promise<boolean> {
        try {
            const data = await this.http.post('api:///query/EmailTest', { email: email }).toPromise();
            let countUser = false;
            if (data) {
                countUser = true;
            }
            return countUser;
        } catch (error) {
            throw error;
        }
    }

    async isOrganizationAlreadyAdded(organizationName): Promise<boolean> {
        try {
            const data = await this.http.post('api:///query/OrganizationTest', { organizationName: organizationName }).toPromise();
            let countOrganization = false;
            if (data) {
                countOrganization = true;
            }
            return countOrganization;
        } catch (error) {
            throw error;
        }
    }
}
